import { useState, useEffect, useRef, useCallback } from "react";
import io from "socket.io-client";
import { socketConfig } from "../../../utils/authConfig";
import { playMicStartCueA, playMicStopCueA } from "../../../mic-sounds/micCues";

/**
 * Hook for MDT (Multidisciplinary Team) recording using Soniox multi-speaker diarization.
 * Uses startAudioMdt and receives transcription events with speaker id.
 */
export function useMDTSpeech(options = {}) {
  const { language = "en-US" } = options;
  const [segments, setSegments] = useState([]); // { speaker, text } final segments
  const [streamingSegment, setStreamingSegment] = useState({ speaker: null, text: "" });
  const [isRecording, setIsRecording] = useState(false);
  const [isConnecting, setIsConnecting] = useState(false);
  const [error, setError] = useState(null);
  const socketRef = useRef(null);
  const listenersSetupRef = useRef(false);

  const setupListeners = useCallback((socket) => {
    if (listenersSetupRef.current && socket === socketRef.current) return;
    if (socket !== socketRef.current && socketRef.current) {
      socketRef.current.removeAllListeners();
      listenersSetupRef.current = false;
    }

    socket.on("connect", () => {
      setIsConnecting(false);
      setError(null);
    });
    socket.on("connect_error", (err) => {
      setIsConnecting(false);
      setError(err.message || "Connection failed");
    });
    socket.on("disconnect", () => {});

    socket.on(
      "transcription",
      ({ transcript, isFinal, speaker }) => {
        const speakerId = speaker != null ? String(speaker) : null;
        if (isFinal && transcript && transcript.trim()) {
          setSegments((prev) => [
            ...prev,
            { speaker: speakerId, text: transcript.trim() },
          ]);
          setStreamingSegment({ speaker: null, text: "" });
        } else if (!isFinal && transcript) {
          setStreamingSegment({ speaker: speakerId, text: transcript });
        }
      }
    );
    listenersSetupRef.current = true;
  }, []);

  const connectAndStart = useCallback(async () => {
    if (!socketRef.current) {
      const socket = io(socketConfig.url, {
        ...socketConfig.options,
        autoConnect: false,
      });
      socketRef.current = socket;
      setupListeners(socket);
    }

    const socket = socketRef.current;
    if (socket.connected) {
      socket.emit("setLanguage", { language });
      socket.emit("startAudioMdt");
      setIsRecording(true);
      playMicStartCueA();
      setError(null);
      return socket;
    }

    setIsConnecting(true);
    setError(null);
    socket.connect();

    return new Promise((resolve, reject) => {
      const timeout = setTimeout(() => {
        reject(new Error("Connection timeout"));
        setIsConnecting(false);
      }, 10000);

      socket.once("connect", () => {
        clearTimeout(timeout);
        setIsConnecting(false);
        socket.emit("setLanguage", { language });
        socket.emit("startAudioMdt");
        setIsRecording(true);
        playMicStartCueA();
        resolve(socket);
      });

      socket.once("connect_error", (err) => {
        clearTimeout(timeout);
        setIsConnecting(false);
        setError(err.message || "Connection failed");
        reject(err);
      });
    });
  }, [language, setupListeners]);

  const stopRecording = useCallback(() => {
    if (isRecording) {
      playMicStopCueA();
    }
    if (socketRef.current && socketRef.current.connected) {
      socketRef.current.emit("endAudio");
      socketRef.current.disconnect();
    }
    setIsRecording(false);
  }, [isRecording]);

  const disconnect = useCallback(() => {
    if (socketRef.current) {
      if (socketRef.current.connected) {
        socketRef.current.emit("endAudio");
        socketRef.current.disconnect();
      }
      socketRef.current.removeAllListeners();
      socketRef.current = null;
      listenersSetupRef.current = false;
    }
    setIsRecording(false);
    setIsConnecting(false);
  }, []);

  const sendAudioChunk = useCallback((chunk) => {
    if (socketRef.current?.connected) {
      socketRef.current.emit("audioData", chunk);
    }
  }, []);

  const clearTranscript = useCallback(() => {
    setSegments([]);
    setStreamingSegment({ speaker: null, text: "" });
  }, []);

  // Full transcript as plain text with speaker labels (for API)
  const getTranscriptWithSpeakers = useCallback(() => {
    const lines = segments.map((s) => {
      const label = s.speaker != null ? `Speaker ${s.speaker}` : "Speaker";
      return `${label}: ${s.text}`;
    });
    if (streamingSegment.text) {
      const label = streamingSegment.speaker != null ? `Speaker ${streamingSegment.speaker}` : "Speaker";
      lines.push(`${label}: ${streamingSegment.text}`);
    }
    return lines.join("\n\n");
  }, [segments, streamingSegment]);

  useEffect(() => {
    return () => {
      disconnect();
    };
  }, [disconnect]);

  return {
    segments,
    streamingSegment,
    isRecording,
    isConnecting,
    error,
    connectAndStart,
    stopRecording,
    disconnect,
    sendAudioChunk,
    clearTranscript,
    getTranscriptWithSpeakers,
  };
}
